import {
  useLazyGetSessionsAnalyticsActivityQuery,
  useLazyGetSessionsSummaryQuery,
} from '@carboncopyco/data';
import type {
  GetSessionsSummaryArgs,
  GetSessionsSummaryResponse,
} from '@carboncopyco/data';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect } from 'react';
import {
  calculatePercentageDifference,
  extractDataForChart,
} from '../../Charts';
import { useDashboardProps } from '../DashboardContext';

type GetTotalDataProps = {
  resourceTypes: GetSessionsSummaryArgs['resourceTypes'];
  assetType: GetSessionsSummaryArgs['assetType'];
};

type Accessors = keyof GetSessionsSummaryResponse['values'];

export const useGetTotalsData = ({
  resourceTypes,
  assetType,
}: GetTotalDataProps) => {
  const { organizationId, short, internal, selectedWorkspaceIds, timeRange } =
    useDashboardProps();
  const [getSessionsSummary, { data, isLoading, isError }] =
    useLazyGetSessionsSummaryQuery();
  const [
    getSessionsSummaryTrend,
    { data: prevData, isLoading: isLoadingPrevData },
  ] = useLazyGetSessionsSummaryQuery();
  const [getSessionsSummaryActivity, { data: chartData }] =
    useLazyGetSessionsAnalyticsActivityQuery();
  const resourceTypesString = JSON.stringify(resourceTypes);

  useEffect(() => {
    getSessionsSummary({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      resourceTypes,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      assetType,
    });
    getSessionsSummaryActivity({
      organizationId,
      resourceTypes,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resolution: 'day',
      shortSessions: short,
      internalSessions: internal,
    });
  }, [
    organizationId,
    short,
    internal,
    selectedWorkspaceIds,
    timeRange,
    getSessionsSummary,
    resourceTypesString,
    getSessionsSummaryActivity,
    assetType,
  ]);

  useEffect(() => {
    getSessionsSummaryTrend({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      resourceTypes,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange * 2),
      end: getDateFromDaysAgo(timeRange),
      timezone: getTimezoneName(),
      assetType,
    });
  }, [
    organizationId,
    short,
    internal,
    timeRange,
    resourceTypesString,
    getSessionsSummaryTrend,
    assetType,
    selectedWorkspaceIds,
  ]);

  const getPercentages = (type: Accessors) => {
    if (type && data?.values?.[type] && prevData?.values?.[type]) {
      const percentagesByType = {
        [type]: calculatePercentageDifference(
          data.values[type] ?? 0,
          prevData.values[type] ?? 0
        ),
      };
      return percentagesByType[type];
    }
    return 0;
  };

  const getDescription = (type: Accessors) => {
    if (!prevData?.values[type]) {
      return '';
    }
    if (getPercentages(type) === 0) {
      return `No change from previous ${timeRange} days`;
    }
    if (!getPercentages(type)) {
      return 'No activity in the previous period';
    }
    return 'from last period';
  };

  return {
    data,
    isLoading,
    prevData,
    isLoadingPrevData,
    getPercentages,
    getDescription,
    chartData: extractDataForChart(chartData?.results),
    isError,
  };
};
